import { useState, useEffect } from 'react';
import { client } from '../../api/client';
import { useToast } from '../common/Toast';

interface ListingDuration {
  id: number;
  days: number;
  tokens: number;
  is_active: boolean;
  for_auction_only: boolean;
  sort_order: number;
}

interface TokenPackage {
  id: number;
  tokens: number;
  price_rsd: number;
  is_popular: boolean;
  is_active: boolean;
  sort_order: number;
}

export function PricingTab() {
  const toast = useToast();
  const [durations, setDurations] = useState<ListingDuration[]>([]);
  const [packages, setPackages] = useState<TokenPackage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Form states
  const [showDurationForm, setShowDurationForm] = useState(false);
  const [showPackageForm, setShowPackageForm] = useState(false);
  const [editingDuration, setEditingDuration] = useState<ListingDuration | null>(null);
  const [editingPackage, setEditingPackage] = useState<TokenPackage | null>(null);
  
  const [durationForm, setDurationForm] = useState({ days: '', tokens: '', sort_order: '', for_auction_only: false });
  const [packageForm, setPackageForm] = useState({ tokens: '', price_rsd: '', is_popular: false, sort_order: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [durRes, pkgRes] = await Promise.all([
        client.api.fetch('/admin/listing-durations'),
        client.api.fetch('/admin/token-packages')
      ]);
      
      const durData = await durRes.json();
      const pkgData = await pkgRes.json();
      
      if (durData.success) setDurations(durData.data || []);
      if (pkgData.success) setPackages(pkgData.data || []);
    } catch (err) {
      setError('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  // Duration handlers
  const handleSaveDuration = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        days: parseInt(durationForm.days),
        tokens: parseInt(durationForm.tokens),
        sort_order: parseInt(durationForm.sort_order) || 0,
        for_auction_only: durationForm.for_auction_only
      };
      
      let res;
      if (editingDuration) {
        res = await client.api.fetch(`/admin/listing-durations/${editingDuration.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        res = await client.api.fetch('/admin/listing-durations', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      
      const data = await res.json();
      if (data.success) {
        loadData();
        setShowDurationForm(false);
        setEditingDuration(null);
        setDurationForm({ days: '', tokens: '', sort_order: '', for_auction_only: false });
        toast.success('Uspešno', editingDuration ? 'Trajanje ažurirano.' : 'Trajanje kreirano.');
      } else {
        toast.error('Greška', data.error || 'Greška pri čuvanju');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDuration = async (id: number) => {
    toast.showConfirm({
      title: 'Brisanje trajanja',
      message: 'Da li ste sigurni da želite da obrišete ovo trajanje?',
      confirmText: 'Obriši',
      cancelText: 'Otkaži',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await client.api.fetch(`/admin/listing-durations/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            loadData();
            toast.success('Uspešno', 'Trajanje obrisano.');
          } else {
            toast.error('Greška', data.error || 'Greška pri brisanju');
          }
        } catch (err) {
          toast.error('Greška', 'Greška pri povezivanju');
        }
      }
    });
  };

  const handleToggleDuration = async (duration: ListingDuration) => {
    try {
      const res = await client.api.fetch(`/admin/listing-durations/${duration.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !duration.is_active })
      });
      const data = await res.json();
      if (data.success) {
        loadData();
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri promeni statusa');
    }
  };

  // Package handlers
  const handleSavePackage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const payload = {
        tokens: parseInt(packageForm.tokens),
        price_rsd: parseFloat(packageForm.price_rsd),
        is_popular: packageForm.is_popular,
        sort_order: parseInt(packageForm.sort_order) || 0
      };
      
      let res;
      if (editingPackage) {
        res = await client.api.fetch(`/admin/token-packages/${editingPackage.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        res = await client.api.fetch('/admin/token-packages', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }
      
      const data = await res.json();
      if (data.success) {
        loadData();
        setShowPackageForm(false);
        setEditingPackage(null);
        setPackageForm({ tokens: '', price_rsd: '', is_popular: false, sort_order: '' });
        toast.success('Uspešno', editingPackage ? 'Paket ažuriran.' : 'Paket kreiran.');
      } else {
        toast.error('Greška', data.error || 'Greška pri čuvanju');
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri povezivanju');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePackage = async (id: number) => {
    toast.showConfirm({
      title: 'Brisanje paketa',
      message: 'Da li ste sigurni da želite da obrišete ovaj paket?',
      confirmText: 'Obriši',
      cancelText: 'Otkaži',
      type: 'danger',
      onConfirm: async () => {
        try {
          const res = await client.api.fetch(`/admin/token-packages/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) {
            loadData();
            toast.success('Uspešno', 'Paket obrisan.');
          } else {
            toast.error('Greška', data.error || 'Greška pri brisanju');
          }
        } catch (err) {
          toast.error('Greška', 'Greška pri povezivanju');
        }
      }
    });
  };

  const handleTogglePackage = async (pkg: TokenPackage) => {
    try {
      const res = await client.api.fetch(`/admin/token-packages/${pkg.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !pkg.is_active })
      });
      const data = await res.json();
      if (data.success) {
        loadData();
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri promeni statusa');
    }
  };

  const handleTogglePopular = async (pkg: TokenPackage) => {
    try {
      const res = await client.api.fetch(`/admin/token-packages/${pkg.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_popular: !pkg.is_popular })
      });
      const data = await res.json();
      if (data.success) {
        loadData();
      }
    } catch (err) {
      toast.error('Greška', 'Greška pri promeni');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      )}

      {/* Trajanje oglasa */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Trajanje oglasa</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Cena u tokenima za različita trajanja oglasa</p>
          </div>
          <button
            onClick={() => {
              setEditingDuration(null);
              setDurationForm({ days: '', tokens: '', sort_order: String(durations.length + 1), for_auction_only: false });
              setShowDurationForm(true);
            }}
            className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
          >
            + Dodaj trajanje
          </button>
        </div>

        {showDurationForm && (
          <form onSubmit={handleSaveDuration} className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Broj dana</label>
                <input
                  type="number"
                  value={durationForm.days}
                  onChange={(e) => setDurationForm({ ...durationForm, days: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Cena (tokeni)</label>
                <input
                  type="number"
                  value={durationForm.tokens}
                  onChange={(e) => setDurationForm({ ...durationForm, tokens: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Redosled</label>
                <input
                  type="number"
                  value={durationForm.sort_order}
                  onChange={(e) => setDurationForm({ ...durationForm, sort_order: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  min="0"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <input
                type="checkbox"
                id="for_auction_only"
                checked={durationForm.for_auction_only}
                onChange={(e) => setDurationForm({ ...durationForm, for_auction_only: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300 dark:border-slate-600"
              />
              <label htmlFor="for_auction_only" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                Samo za aukcije (Ponuda i Potražnja neće videti ovu opciju)
              </label>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Čuvanje...' : editingDuration ? 'Sačuvaj' : 'Dodaj'}
              </button>
              <button
                type="button"
                onClick={() => { setShowDurationForm(false); setEditingDuration(null); }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500"
              >
                Otkaži
              </button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Trajanje</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Cena (tokeni)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Tip</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {durations.map((d) => (
                <tr key={d.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${!d.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3 text-slate-900 dark:text-white font-medium">{d.days} dana</td>
                  <td className="px-4 py-3 text-amber-600 dark:text-amber-400 font-bold">{d.tokens} tokena</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      d.for_auction_only
                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                        : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                    }`}>
                      {d.for_auction_only ? 'Samo aukcije' : 'Svi tipovi'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      d.is_active 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {d.is_active ? 'Aktivno' : 'Neaktivno'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingDuration(d);
                          setDurationForm({ 
                            days: String(d.days), 
                            tokens: String(d.tokens), 
                            sort_order: String(d.sort_order),
                            for_auction_only: Boolean(d.for_auction_only)
                          });
                          setShowDurationForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                      >
                        Izmeni
                      </button>
                      <button
                        onClick={() => handleToggleDuration(d)}
                        className="text-yellow-600 hover:text-yellow-500 text-sm font-medium"
                      >
                        {d.is_active ? 'Deaktiviraj' : 'Aktiviraj'}
                      </button>
                      <button
                        onClick={() => handleDeleteDuration(d.id)}
                        className="text-red-600 hover:text-red-500 text-sm font-medium"
                      >
                        Obriši
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Paketi tokena */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Paketi tokena</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">Cene paketa tokena u RSD</p>
          </div>
          <button
            onClick={() => {
              setEditingPackage(null);
              setPackageForm({ tokens: '', price_rsd: '', is_popular: false, sort_order: String(packages.length + 1) });
              setShowPackageForm(true);
            }}
            className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 transition-colors"
          >
            + Dodaj paket
          </button>
        </div>

        {showPackageForm && (
          <form onSubmit={handleSavePackage} className="bg-slate-50 dark:bg-slate-700/50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Broj tokena</label>
                <input
                  type="number"
                  value={packageForm.tokens}
                  onChange={(e) => setPackageForm({ ...packageForm, tokens: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Cena (RSD)</label>
                <input
                  type="number"
                  step="0.01"
                  value={packageForm.price_rsd}
                  onChange={(e) => setPackageForm({ ...packageForm, price_rsd: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  required
                  min="0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Redosled</label>
                <input
                  type="number"
                  value={packageForm.sort_order}
                  onChange={(e) => setPackageForm({ ...packageForm, sort_order: e.target.value })}
                  className="w-full px-4 py-2 bg-white dark:bg-slate-800 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white"
                  min="0"
                />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={packageForm.is_popular}
                    onChange={(e) => setPackageForm({ ...packageForm, is_popular: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">Popularan paket</span>
                </label>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-amber-500 text-slate-900 rounded-lg font-semibold hover:bg-amber-400 disabled:opacity-50"
              >
                {saving ? 'Čuvanje...' : editingPackage ? 'Sačuvaj' : 'Dodaj'}
              </button>
              <button
                type="button"
                onClick={() => { setShowPackageForm(false); setEditingPackage(null); }}
                className="px-4 py-2 bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-white rounded-lg hover:bg-slate-300 dark:hover:bg-slate-500"
              >
                Otkaži
              </button>
            </div>
          </form>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Tokeni</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Cena (RSD)</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Po tokenu</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 dark:text-slate-300 uppercase">Akcije</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {packages.map((p) => (
                <tr key={p.id} className={`hover:bg-slate-50 dark:hover:bg-slate-700/50 ${!p.is_active ? 'opacity-50' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-900 dark:text-white font-medium">{p.tokens} tokena</span>
                      {!!p.is_popular && (
                        <span className="px-2 py-0.5 text-xs bg-amber-500 text-slate-900 rounded-full font-semibold">
                          POPULARNO
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-green-600 dark:text-green-400 font-bold">
                    {Number(p.price_rsd).toLocaleString('sr-RS')} RSD
                  </td>
                  <td className="px-4 py-3 text-slate-500 dark:text-slate-400">
                    {Math.round(Number(p.price_rsd) / p.tokens)} RSD/token
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      p.is_active 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {p.is_active ? 'Aktivno' : 'Neaktivno'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingPackage(p);
                          setPackageForm({ 
                            tokens: String(p.tokens), 
                            price_rsd: String(p.price_rsd), 
                            is_popular: p.is_popular,
                            sort_order: String(p.sort_order) 
                          });
                          setShowPackageForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                      >
                        Izmeni
                      </button>
                      <button
                        onClick={() => handleTogglePopular(p)}
                        className="text-purple-600 hover:text-purple-500 text-sm font-medium"
                      >
                        {p.is_popular ? 'Ukloni popularno' : 'Označi popularno'}
                      </button>
                      <button
                        onClick={() => handleTogglePackage(p)}
                        className="text-yellow-600 hover:text-yellow-500 text-sm font-medium"
                      >
                        {p.is_active ? 'Deaktiviraj' : 'Aktiviraj'}
                      </button>
                      <button
                        onClick={() => handleDeletePackage(p.id)}
                        className="text-red-600 hover:text-red-500 text-sm font-medium"
                      >
                        Obriši
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
